import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, ShieldCheck, UserPlus, Trash2 } from "lucide-react";

/**
 * Phase 22 — Platform admins tab.
 *
 * Lists every identity with ``platform_role = "platform_admin"``.
 * Grant flow accepts an email; if the identity doesn't exist yet a
 * set-password link is emailed automatically. Revoke is guarded by
 * a confirmation modal and disabled on your own row.
 */
export default function PlatformAdminsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null); // identity obj

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/admins");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grant = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/platform/admins", { email: email.trim() });
      if (data.already_admin) toast("That identity is already a platform admin.");
      else if (data.identity_created) toast.success(`Invited ${email} — set-password link sent.`);
      else toast.success(`${email} promoted to platform admin.`);
      setDialog(false);
      setEmail("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const revoke = async () => {
    if (!confirmRevoke) return;
    try {
      await api.delete(`/platform/admins/${confirmRevoke.id}`);
      toast.success(`${confirmRevoke.email} demoted.`);
      setConfirmRevoke(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // The current authed user is a membership; their identity_id lives on principal.
  // We surface it via /auth/me → user.identity_id (mirrored). Fall back to email.
  const isSelf = (idn) => idn.email === (user?.email || "").toLowerCase();

  return (
    <div className="space-y-4" data-testid="platform-admins-page">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {items.length} platform admin{items.length === 1 ? "" : "s"}
        </div>
        <Button onClick={() => setDialog(true)} data-testid="platform-grant-admin-btn">
          <UserPlus className="mr-1.5 h-4 w-4" /> Grant admin
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Full name</TableHead>
                <TableHead>Last workspace</TableHead>
                <TableHead>Granted</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading admins…
                </TableCell></TableRow>
              )}
              {!loading && items.map((idn) => (
                <TableRow key={idn.id} data-testid={`platform-admin-row-${idn.email}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-secondary" />
                      {idn.email}
                      {isSelf(idn) && <Badge variant="outline" className="text-[10px]">you</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{idn.full_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {idn.last_tenant_id ? idn.last_tenant_id.slice(0, 8) + "…" : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {idn.created_at ? new Date(idn.created_at).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost" size="sm"
                      disabled={isSelf(idn)}
                      onClick={() => setConfirmRevoke(idn)}
                      data-testid={`platform-revoke-admin-${idn.email}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  No platform admins yet — grant the first one.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="platform-grant-admin-dialog">
          <DialogHeader>
            <DialogTitle>Grant platform admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="grant-email">Email</Label>
              <Input
                id="grant-email" type="email" autoComplete="off"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                data-testid="platform-grant-admin-email"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                If this person doesn't have an account yet, we'll email them a set-password link. Otherwise
                they'll get platform admin added on top of their existing workspaces — no other change.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>Cancel</Button>
            <Button
              onClick={grant}
              disabled={saving || !email.trim()}
              data-testid="platform-grant-admin-submit"
            >
              {saving ? "Granting…" : "Grant admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmRevoke} onOpenChange={(open) => !open && setConfirmRevoke(null)}>
        <AlertDialogContent data-testid="platform-revoke-admin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke platform admin?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmRevoke?.email} will lose workspace-wide access immediately.
              They'll still be a member of their existing workspaces.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="platform-revoke-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={revoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="platform-revoke-confirm"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
